import { type MutableRefObject, type ReactNode } from 'react'
import { twMerge } from 'tailwind-merge'
import type { AssistantMessage, ToolCall } from '../../../../types'
import QuoteReferenceSelector from '../../QuoteReferenceSelector'
import type { GroupContentPart } from '../hooks/use-interleaved-parts'
import { AssistantBody, type AssistantBodyEditing } from './assistant-body'
import { MessageError } from './message-error'
import { messageStyles } from './message-markdown'
import { Visualization } from './visualization'

export interface AssistantMessageChromeProps {
  message: AssistantMessage
  isSubmessage: boolean
  /** Whether this chat's turn is live (loading, streaming, or running tools). */
  isTurnActive: boolean
  isMostRecent?: boolean
  editing?: AssistantBodyEditing
  renderedContentRef?: MutableRefObject<HTMLDivElement | null>
  groupToolCalls?: Array<ToolCall>
  groupContentParts?: Array<GroupContentPart>
  /** What renders under the body, inside the bubble: actions row and artifacts on primary rows, the hover-target spacer on submessages. */
  children?: ReactNode
}

/**
 * The chrome every assistant message shares — primary rows and expanded
 * submessages alike: the bubble wrapper with its id/data attributes, the
 * quote selector, the body, and the trailing error + visualization. Keeping
 * it in one place means the two row kinds cannot drift structurally.
 */
export function AssistantMessageChrome({
  message,
  isSubmessage,
  isTurnActive,
  isMostRecent,
  editing,
  renderedContentRef,
  groupToolCalls,
  groupContentParts,
  children,
}: AssistantMessageChromeProps) {
  return (
    <>
      <div
        className={twMerge(
          messageStyles({ role: message.role, isSubmessage, hasNoAttachment: true }),
          'group/message relative pb-0',
          isSubmessage ? 'pt-0' : 'pb-4',
        )}
        id={`message-${message.id}`}
        data-message-role={message.role}
        data-message-id={message.id}
      >
        {!isTurnActive && <QuoteReferenceSelector messageId={message.id} />}
        <div className="flex flex-col">
          <AssistantBody
            message={message}
            isMostRecent={isMostRecent}
            isTurnActive={isTurnActive}
            editing={editing}
            renderedContentRef={renderedContentRef}
            groupToolCalls={groupToolCalls}
            groupContentParts={groupContentParts}
            thinkingContent={message.thinkingContent}
          />
        </div>
        {children}
      </div>

      <MessageError message={message} />

      {message.visualization && <Visualization visualization={message.visualization} />}
    </>
  )
}
